import { type Hex, packed } from '../icons/pix';
import { GOLD, M, RED } from './palette';
import { bez, type Pen, type Pt, spline } from './pen';
import type { PlateAnim, Tones } from './registry';

/**
 * The plates' shared ornaments, in units, in the master's manner: flat cel shading in two or three
 * tones lit from the upper right, clean shapes, gold fittings and red tassels. Layers are outlined in
 * ink when the plate is put together (plates/compose.ts), so a motif draws only its colours.
 */

/**
 * Cel shading: draws a shape in its base tone on a layer of its own, then gives the pixels on its
 * upper-right edge the light tone and those on its lower-left edge the shade, `band` units deep,
 * and lays it over `p`.
 */
export function cel(p: Pen, tones: Tones, draw: (q: Pen, c: Hex) => void, band = 1): void {
  const q = p.layer();
  draw(q, tones[1]);
  const { pix, s } = q;
  const base = packed(tones[1]);
  const hi = packed(tones[0]);
  const lo = packed(tones[2]);
  const d = Math.max(1, Math.round(band * s));
  const src = pix.d.slice();
  const on = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < pix.w && y < pix.h && src[y * pix.w + x]! >>> 24 > 0;
  for (let y = 0; y < pix.h; y++)
    for (let x = 0; x < pix.w; x++) {
      if (src[y * pix.w + x] !== base) continue;
      const lit = !on(x + d, y) || !on(x, y - d);
      const dark = !on(x - d, y) || !on(x, y + d);
      if (lit && !dark) pix.d[y * pix.w + x] = hi;
      else if (dark && !lit) pix.d[y * pix.w + x] = lo;
    }
  p.over(q);
}

/** A five-petalled blossom (plum, cherry, golden flower): petals lit upper right, a gold heart. */
export function blossom(
  p: Pen,
  cx: number,
  cy: number,
  r: number,
  tones: Tones,
  heart: Hex = M.goldHi,
): void {
  cel(p, tones, (q, c) => {
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
      q.disc(cx + Math.cos(a) * r * 0.52, cy + Math.sin(a) * r * 0.52, r * 0.5, c);
    }
  });
  p.disc(cx, cy, Math.max(0.6, r * 0.24), heart);
}

/** A bud: a small round of the blossom's colour. */
export function bud(p: Pen, x: number, y: number, tones: Tones): void {
  p.rect(x, y, 2, 2, tones[1]).px(x + 1, y, tones[0]);
}

/** A falling petal, two units. */
export function petal(p: Pen, x: number, y: number, tones: Tones): void {
  p.rect(x, y, 2, 1, tones[1])
    .px(x + 1, y, tones[0])
    .px(x, y + 1, tones[2]);
}

/** A branch: a tapering stroke through the points, in dark bark. */
export function branch(p: Pen, pts: readonly Pt[], w0: number, w1: number, c: Hex = M.brown): void {
  p.taper(spline(pts, 5), w0, w1, c);
}

/** A spiral curl, `r` units out, winding in (clockwise unless `flip`): a cloud's or a wave's curl. */
export function curl(p: Pen, cx: number, cy: number, r: number, c: Hex, flip = false): void {
  const pts: Pt[] = [];
  const n = Math.max(10, Math.ceil(r * 6));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = Math.PI * (0.9 + t * 1.9) * (flip ? -1 : 1) + (flip ? Math.PI : 0);
    const rr = r * (1 - t * 0.75);
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
  }
  p.path(pts, c);
}

/**
 * A ruyi cloud (祥云): a big round puff with a curl, smaller puffs either side, a flat underside,
 * `w` units wide, its bottom left corner at (x, y); `flip` mirrors it.
 */
export function cloud(p: Pen, x: number, y: number, w: number, tones: Tones, flip = false): void {
  const X = (u: number) => (flip ? x + w - u : x + u);
  const big = w * 0.27;
  const mid = w * 0.2;
  const small = w * 0.15;
  const puffs: [number, number, number][] = [
    [X(w * 0.42), y - big, big],
    [X(w * 0.72), y - mid * 0.95, mid],
    [X(w * 0.15), y - small, small],
    [X(w * 0.9), y - small * 0.8, small * 0.8],
  ];
  cel(p, tones, (q, c) => {
    for (const [cx, cy, r] of puffs) q.disc(cx, cy, r, c);
    q.rect(x + small * 0.6, y - small, w - small * 1.2, small, c);
  });
  const [bx, by, br] = puffs[0]!;
  if (br >= 2.5) curl(p, bx, by, br * 0.62, tones[2], flip);
  const [mx, my, mr] = puffs[1]!;
  if (mr >= 3) curl(p, mx, my, mr * 0.55, tones[2], flip);
  // The line where the puffs meet the underside.
  p.line(x + small, y - 1, x + w - small, y - 1, tones[2]);
}

/** A round moon: a pale disc lit upper right, with soft grey seas. */
export function moon(p: Pen, cx: number, cy: number, r: number, tones: Tones): void {
  cel(p, tones, (q, c) => q.disc(cx, cy, r, c), Math.max(1, r / 5));
  p.disc(cx - r * 0.3, cy - r * 0.15, r * 0.22, tones[2]);
  p.disc(cx + r * 0.2, cy + r * 0.35, r * 0.16, tones[2]);
  p.disc(cx + r * 0.35, cy - r * 0.35, r * 0.1, tones[2]);
}

/** Fire's three tones, outside in. */
export const FIRE: Tones = [M.red, '#f0782a', '#ffd25a'];

/**
 * A flame: a licking tongue with a flicked tip and a smaller side tongue, a middle one and a hot
 * core inside; `lean` bends it sideways. `tones` from the outside in.
 */
export function flame(
  p: Pen,
  x: number,
  base: number,
  h: number,
  w: number,
  lean = 0,
  tones: Tones = FIRE,
): void {
  const tongue = (k: number, c: Hex, dx = 0, kh = 1) => {
    const hw = (w / 2) * k;
    const top = base - h * k * kh;
    const tip: Pt = [x + dx + lean * 1.4 * k, top];
    const left = bez([x + dx - hw, base], [x + dx - hw * 1.2, base - h * k * 0.5], tip, 9);
    const right = bez(tip, [x + dx + hw * 0.4 + lean * 0.4, base - h * k * 0.55], [x + dx + hw, base], 9);
    p.poly([...left, ...right], c);
  };
  tongue(1, tones[0]);
  tongue(0.55, tones[0], w * 0.45, 0.85);
  tongue(0.45, tones[0], -w * 0.4, 0.75);
  tongue(0.7, tones[1], 0, 0.95);
  tongue(0.38, tones[2], 0, 0.9);
}

/** A straight sword from its point to its pommel: steel blade, gold guard, maroon grip, red tassel. */
export function sword(
  p: Pen,
  tip: Pt,
  guard: Pt,
  o: { grip?: number; tassel?: boolean; blade?: Tones } = {},
): void {
  const [bh, bm, bl] = o.blade ?? ['#f4f4f6', '#c8ccd6', '#7e8494'];
  const dx = guard[0] - tip[0];
  const dy = guard[1] - tip[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // Blade: shaded edge, base, a light edge on its upper-right side.
  p.taper([tip, guard], 1, 3, bl);
  p.taper(
    [
      [tip[0] + uy * 0.3, tip[1] - ux * 0.3],
      [guard[0] + uy * 0.5, guard[1] - ux * 0.5],
    ],
    0.6,
    2,
    bm,
  );
  p.line(tip[0] + uy * 0.8, tip[1] - ux * 0.8, guard[0] + uy * 1, guard[1] - ux * 1, bh);
  // Guard across the blade.
  const gx = -uy * 3;
  const gy = ux * 3;
  p.line(guard[0] - gx, guard[1] - gy, guard[0] + gx, guard[1] + gy, M.gold, 1.6);
  p.px(guard[0] + gx * 0.6, guard[1] + gy * 0.6 - 0.5, M.goldHi);
  // Grip and pommel.
  const g = o.grip ?? 6;
  const end: Pt = [guard[0] + ux * g, guard[1] + uy * g];
  p.line(guard[0] + ux, guard[1] + uy, end[0], end[1], M.maroon, 1.6);
  for (let i = 1.5; i < g; i += 1.5) p.px(guard[0] + ux * i, guard[1] + uy * i, M.maroonHi);
  p.disc(end[0] + ux * 0.6, end[1] + uy * 0.6, 1.1, M.gold);
  if (o.tassel !== false) {
    const t0: Pt = [end[0] + ux * 1.5, end[1] + uy * 1.5];
    p.path(bez(t0, [t0[0] - 1, t0[1] + 3], [t0[0] - 0.5, t0[1] + 6], 6), M.red, 1.2);
    p.rect(t0[0] - 1.5, t0[1] + 5, 2, 3, M.red).px(t0[0] - 0.5, t0[1] + 5, M.redHi);
  }
}

/** A spear upright from `top` (its point) down to `bottom`: dark shaft, steel head, a red tuft. */
export function spear(p: Pen, x: number, top: number, bottom: number, lean = 0): void {
  const at = (y: number): number => x + ((y - top) / (bottom - top)) * lean;
  p.line(at(top + 6), top + 6, at(bottom), bottom, M.brown, 1.4);
  p.line(at(top + 7), top + 7, at(bottom), bottom, M.brownHi, 0.6);
  p.poly(
    [
      [at(top), top],
      [at(top) + 1.5, top + 4],
      [at(top), top + 6],
      [at(top) - 1.5, top + 4],
    ],
    '#c8ccd6',
  );
  p.line(at(top) + 0.5, top + 1, at(top) + 0.5, top + 4.5, '#f4f4f6');
  cel(p, RED, (q, c) => {
    q.poly(
      [
        [at(top + 6) - 2, top + 6],
        [at(top + 6) + 2, top + 6],
        [at(top + 6) + 3, top + 11],
        [at(top + 6) - 2.5, top + 10],
      ],
      c,
    );
  });
  p.rect(at(top + 6) - 1.5, top + 6, 3, 1, M.gold);
}

/** A bamboo stalk from y0 down to y1, `w` units thick, with nodes every `node` units. */
export function bamboo(p: Pen, x: number, y0: number, y1: number, w: number, tones: Tones, node = 7): void {
  cel(p, tones, (q, c) => q.rect(x, y0, w, y1 - y0, c));
  for (let y = y0 + node * 0.6; y < y1 - 1; y += node) {
    p.rect(x, y, w, 1, tones[2]);
    p.rect(x - 0.5, y - 1, w + 1, 1, tones[0]);
  }
}

/** A pointed leaf from its stem at (x, y) along angle `a` (radians), `len` long and `w` wide. */
export function leaf(p: Pen, x: number, y: number, a: number, len: number, w: number, tones: Tones): void {
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  const nx = -uy;
  const ny = ux;
  const tip: Pt = [x + ux * len, y + uy * len];
  const mid = (k: number): Pt => [x + ux * len * 0.4 + nx * w * k, y + uy * len * 0.4 + ny * w * k];
  cel(p, tones, (q, c) => q.poly([[x, y], mid(0.5), tip, mid(-0.5)], c));
  p.line(x, y, x + ux * len * 0.7, y + uy * len * 0.7, tones[2]);
}

/** A hanging cluster of wisteria from (x, y), `len` units long, its blooms shrinking downwards. */
export function wisteria(p: Pen, x: number, y: number, len: number, tones: Tones, sway = 0): void {
  for (let j = 0; j < len; j += 1.6) {
    const k = 1 - j / len;
    const cx = x + Math.sin((j / len) * Math.PI) * sway;
    const r = 0.7 + 2.1 * k;
    const c = j % 3 < 1.5 ? tones[1] : tones[0];
    p.ell(cx, y + j, r, 0.9, c);
    if (r > 1.2) p.px(cx - r * 0.7, y + j, tones[2]);
  }
}

/** A lotus flower: pointed petals fanned upwards on a green pad, a gold heart. */
export function lotus(p: Pen, cx: number, cy: number, r: number, tones: Tones, pad = true): void {
  if (pad)
    cel(p, ['#7cc070', '#3f8a46', '#245a30'], (q, c) => q.ell(cx, cy + r * 0.45, r * 1.25, r * 0.38, c));
  const petals = [-1.0, -0.55, 0.55, 1.0, 0];
  for (const a of petals) {
    const ang = -Math.PI / 2 + a * 0.75;
    const ux = Math.cos(ang);
    const uy = Math.sin(ang);
    const base: Pt = [cx + ux * r * 0.1, cy + 0.2 * r];
    const tip: Pt = [cx + ux * r * 1.05, cy + uy * r * 1.05];
    const nx = -uy * r * 0.36;
    const ny = ux * r * 0.36;
    cel(p, tones, (q, c) =>
      q.poly(
        [
          base,
          [base[0] + (tip[0] - base[0]) * 0.45 + nx, base[1] + (tip[1] - base[1]) * 0.45 + ny],
          tip,
          [base[0] + (tip[0] - base[0]) * 0.45 - nx, base[1] + (tip[1] - base[1]) * 0.45 - ny],
        ],
        c,
      ),
    );
    p.px(tip[0] - 0.5, tip[1] + 0.3, '#fff0f4');
  }
  p.ell(cx, cy - r * 0.05, r * 0.32, r * 0.2, M.goldHi);
}

/** A maple leaf: five pointed lobes round a centre, with a stem. */
export function maple(p: Pen, cx: number, cy: number, r: number, tones: Tones, turn = 0): void {
  const pts: Pt[] = [];
  const lobes = [1, 0.45, 0.85, 0.45, 0.7, 0.35, 0.7, 0.45, 0.85, 0.45];
  for (let i = 0; i < lobes.length; i++) {
    const a = -Math.PI / 2 + turn + (i * Math.PI * 2) / lobes.length;
    pts.push([cx + Math.cos(a) * r * lobes[i]!, cy + Math.sin(a) * r * lobes[i]!]);
  }
  cel(p, tones, (q, c) => q.poly(pts, c));
  p.line(cx, cy, cx + Math.sin(turn) * -r * 0.9, cy + Math.cos(turn) * r * 0.9, tones[2]);
}

/**
 * A breaking wave, after the old prints: a tall curl of blue rising from (x, y) to its crest, foam
 * clawing at its lip, `w` wide and `h` tall; `flip` curls it to the left.
 */
export function wave(p: Pen, x: number, y: number, w: number, h: number, tones: Tones, flip = false): void {
  const X = (u: number) => (flip ? x + w - u : x + u);
  const body: Pt[] = [
    [X(0), y],
    ...bez([X(0), y], [X(w * 0.05), y - h * 0.9], [X(w * 0.55), y - h], 10),
    ...bez([X(w * 0.55), y - h], [X(w * 0.95), y - h * 0.95], [X(w * 0.85), y - h * 0.5], 8),
    ...bez([X(w * 0.85), y - h * 0.5], [X(w * 0.55), y - h * 0.6], [X(w * 0.6), y - h * 0.3], 6),
    [X(w), y],
  ];
  cel(p, tones, (q, c) => q.poly(body, c), 1.5);
  // Lines of the swell.
  for (const k of [0.25, 0.5])
    p.path(
      bez(
        [X(w * 0.12 + k * w * 0.3), y],
        [X(w * 0.15 + k * w * 0.2), y - h * 0.6],
        [X(w * 0.5), y - h * (0.75 - k * 0.3)],
        8,
      ),
      tones[0],
    );
  // Foam claws on the lip.
  for (let i = 0; i < 4; i++) {
    const u = w * (0.45 + i * 0.13);
    const v = y - h * (1 - i * 0.12) + (i > 2 ? h * 0.15 : 0);
    p.disc(X(u), v, 1.3, M.whiteHi);
    p.line(X(u), v, X(u + (i % 2 ? 1.5 : 2.5)), v + 2, M.whiteHi);
  }
  p.disc(X(w * 0.72), y - h * 0.62, 1, M.white);
}

/** A rock: a faceted lump lit upper right, with a crack. */
export function rock(p: Pen, x: number, y: number, w: number, h: number, tones: Tones): void {
  cel(
    p,
    tones,
    (q, c) =>
      q.poly(
        [
          [x, y],
          [x + w * 0.08, y - h * 0.6],
          [x + w * 0.35, y - h],
          [x + w * 0.7, y - h * 0.85],
          [x + w, y - h * 0.3],
          [x + w, y],
        ],
        c,
      ),
    1.5,
  );
  p.path(
    [
      [x + w * 0.4, y - h * 0.95],
      [x + w * 0.5, y - h * 0.5],
      [x + w * 0.42, y - h * 0.2],
    ],
    tones[2],
  );
}

/** A pine: a crooked trunk from (x, y) up to (tx, ty), with flat layered pads of needles. */
export function pine(
  p: Pen,
  x: number,
  y: number,
  tx: number,
  ty: number,
  pads: readonly [number, number, number][],
): void {
  branch(
    p,
    [
      [x, y],
      [(x + tx) / 2 + 2, (y + ty) / 2],
      [tx, ty],
    ],
    2.5,
    1.2,
    M.brown,
  );
  for (const [px, py, w] of pads)
    cel(p, ['#6aa060', '#3a6e3e', '#22442a'], (q, c) => q.ell(px, py, w / 2, w / 6 + 1, c));
}

/** A rolled scroll end standing upright at x: a paper roll with dark wooden knobs. */
export function scrollEnd(p: Pen, x: number, y0: number, y1: number): void {
  cel(p, ['#fbf7f2', '#e2d8cc', '#b8a898'], (q, c) => q.rect(x - 2, y0, 4, y1 - y0, c));
  p.rect(x - 1.5, y0 - 2, 3, 2, M.brownHi).rect(x - 1.5, y1, 3, 2, M.brownHi);
  p.px(x, y0 - 2, M.goldHi).px(x, y1 + 1, M.goldLo);
}

/** A gold charm with a red cord hung from (x, y): on the master's sword and sash. */
export function hangingCharm(p: Pen, x: number, y: number, cord = 4, tones: Tones = GOLD): void {
  p.line(x, y, x, y + cord, M.redLo);
  p.rect(x - 1, y + cord, 3, 3, tones[1])
    .px(x + 1, y + cord, tones[0])
    .px(x - 1, y + cord + 2, tones[2]);
  p.rect(x - 1, y + cord + 3, 3, 4, M.red).px(x + 1, y + cord + 3, M.redHi);
}

/** Torn cloth draped from (x, y) along `w`, `h` deep, its hem ragged: the challenger's banner. */
export function cloth(p: Pen, x: number, y: number, w: number, h: number, tones: Tones, seed = 1): void {
  const pts: Pt[] = [
    [x, y],
    [x + w, y],
  ];
  const n = Math.max(3, Math.round(w / 2));
  for (let i = n; i >= 0; i--) {
    const u = (i / n) * w;
    const rag = ((i * 7 + seed * 5) % 5) - 2;
    pts.push([x + u, y + h + rag * 0.9 + (i % 2 ? -1.5 : 0.5)]);
  }
  cel(p, tones, (q, c) => q.poly(pts, c), 1);
  for (let i = 1; i < n; i += 2) p.line(x + (i / n) * w, y + 1, x + (i / n) * w + 0.5, y + h * 0.7, tones[2]);
}

/**
 * Light motion: `n` sprites drifting from y0 to y1 (falling petals, snow, rising foxfire) across a
 * band `w` units wide from x, swaying sideways, fading in and out at the ends of their run.
 */
export function drift(
  a: PlateAnim,
  name: string,
  t: number,
  n: number,
  x: number,
  w: number,
  y0: number,
  y1: number,
  o: { speed?: number; sway?: number; seed?: number; slant?: number } = {},
): void {
  const speed = o.speed ?? 0.3;
  const seed = o.seed ?? 0;
  for (let i = 0; i < n; i++) {
    const ph = (t * speed + (i + seed * 0.37) / n) % 1;
    const px =
      x +
      (((i * 37 + seed * 11) % 100) / 100) * w +
      Math.sin((t + i) * 2.3) * (o.sway ?? 2) +
      ph * (o.slant ?? 0);
    a.spr(name, px, y0 + ph * (y1 - y0), Math.min(1, (1 - ph) * 4, ph * 8));
  }
}
